import React, { useEffect, createElement } from 'react';
import { ArrowRightIcon, SearchIcon, FileTextIcon, ShareIcon, MailIcon, TrendingUpIcon, CheckCircleIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteMarketingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website marketing | Meer bezoekers & klanten | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Website marketing strategieën voor meer bezoekers en klanten. Van SEO en content marketing tot social media en e-mail. Praktische tips van Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website marketing | Meer bezoekers & klanten | Noord',
      'og:description': 'Website marketing strategieën voor meer bezoekers en klanten. Van SEO en content marketing tot social media en e-mail.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-marketing',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2576">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2577">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2578">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2579" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2580" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2581" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2582">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2583">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2584">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2585">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2586">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2587">/</span>
            <span className="text-accent-light font-medium" data-id="element-2588">
              Website marketing
            </span>
          </nav>
          <p className="text-accent-light font-semibold text-sm tracking-widest uppercase mb-4 animate-fade-in-up" data-id="element-2589">
            Kennisbank
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up-delay-1" data-id="element-2590">
            Website marketing
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-2" data-id="element-2591">
            Hoe je meer bezoekers en klanten haalt uit je website met slimme
            online marketing.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24 bg-white" data-id="element-2592">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2593">
          <div className="prose prose-lg prose-slate max-w-none" data-id="element-2594">
            <h2 className="text-3xl font-bold text-navy-900 mb-6" data-id="element-2595">
              Wat is website marketing?
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6" data-id="element-2596">
              Een mooie website is pas het begin. Zonder bezoekers levert zelfs
              de beste website geen klanten op. Website marketing (of online
              marketing) is het geheel van strategieën en technieken om gericht
              verkeer naar je website te trekken en deze bezoekers om te zetten
              in betalende klanten.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8" data-id="element-2597">
              Het doel is niet alleen méér bezoekers, maar vooral de júiste
              bezoekers. Mensen die daadwerkelijk op zoek zijn naar jouw
              diensten of producten.
            </p>

            <div className="bg-navy-50 p-8 rounded-2xl border border-navy-100 mb-12" data-id="element-2598">
              <h3 className="text-xl font-bold text-navy-900 mb-4 flex items-center gap-2" data-id="element-2599">
                <TrendingUpIcon className="w-6 h-6 text-accent" data-id="element-2600" />
                Waarom is het belangrijk?
              </h3>
              <ul className="space-y-3" data-id="element-2601">
                <li className="flex items-start gap-3" data-id="element-2602">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2603" />
                  <span className="text-slate-700" data-id="element-2604">
                    Verhoogt je online zichtbaarheid in zoekmachines
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2605">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2606" />
                  <span className="text-slate-700" data-id="element-2607">
                    Bouwt vertrouwen en autoriteit op in jouw vakgebied
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2608">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2609" />
                  <span className="text-slate-700" data-id="element-2610">
                    Zorgt voor een continue stroom van nieuwe leads
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2611">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2612" />
                  <span className="text-slate-700" data-id="element-2613">
                    Meetbaar rendement op je investering (ROI)
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Strategies Section */}
      <section className="py-24 bg-surface-muted" data-id="element-2614">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2615">
          <div className="text-center mb-16" data-id="element-2616">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2617">
              Marketing strategieën
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2618">
              De 4 belangrijkste pijlers om meer uit je website te halen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2619">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2620">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2621">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-2622" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2623">
                SEO optimalisatie
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2624">
                Zorg dat je website organisch gevonden wordt in Google. Door de
                juiste zoekwoorden te gebruiken en je website technisch te
                optimaliseren, trek je gratis relevant verkeer aan.
              </p>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2625">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2626">
                <FileTextIcon className="w-6 h-6 text-accent" data-id="element-2627" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2628">
                Content marketing
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2629">
                Trek bezoekers aan met waardevolle content. Blogs, kennisbank
                artikelen en cases laten je expertise zien en helpen potentiële
                klanten bij hun oriëntatieproces.
              </p>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2630">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2631">
                <ShareIcon className="w-6 h-6 text-accent" data-id="element-2632" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2633">
                Social media
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2634">
                Vergroot je bereik via kanalen zoals LinkedIn, Instagram of
                Facebook. Deel je website content en stuur geïnteresseerd
                verkeer direct naar je landingspagina's.
              </p>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2635">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2636">
                <MailIcon className="w-6 h-6 text-accent" data-id="element-2637" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2638">
                E-mail marketing
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2639">
                Bouw een relatie op met je doelgroep. Verzamel e-mailadressen
                via je website en stuur gerichte nieuwsbrieven om top-of-mind te
                blijven bij je potentiële klanten.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-2640">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2641">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2642">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2643">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2644">
              Website laten maken
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2645">•</span>
            <a href="#/website-tips" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2646">
              Website tips
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2647">•</span>
            <a href="#/kennisbank" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2648">
              Kennisbank
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2649">•</span>
            <a href="#/" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2650">
              Homepage
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2651" />
    </main>;
}